import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'

interface FeedbackState {
  step: 'initial' | 'clarifying' | 'categorized' | 'submitting' | 'success' | 'error'
  userMessage: string
  category: string | null
  clarifyingQuestions: string[]
  clarifyingAnswers: string[]
  suggestedContext: string[]
  enrichedSummary: string
  currentQuestion: number
}

const OLLAMA_URL = 'http://flash.local:11434'

export default function FeedbackModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user } = useAuth()
  const [state, setState] = useState<FeedbackState>({
    step: 'initial',
    userMessage: '',
    category: null,
    clarifyingQuestions: [],
    clarifyingAnswers: [],
    suggestedContext: [],
    enrichedSummary: '',
    currentQuestion: 0
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentAnswer, setCurrentAnswer] = useState('')

  useEffect(() => {
    if (!isOpen) {
      setState({
        step: 'initial',
        userMessage: '',
        category: null,
        clarifyingQuestions: [],
        clarifyingAnswers: [],
        suggestedContext: [],
        enrichedSummary: '',
        currentQuestion: 0
      })
      setCurrentAnswer('')
    }
  }, [isOpen])

  const analyzeWithAI = async (prompt: string): Promise<string> => {
    try {
      const response = await fetch(`${OLLAMA_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'llama3.3:70b',
          prompt,
          stream: false,
          options: { temperature: 0.3 }
        })
      })
      const data = await response.json()
      return data.response
    } catch (error) {
      console.error('AI analysis failed:', error)
      throw error
    }
  }

  const handleInitialSubmit = async () => {
    if (!state.userMessage.trim()) return
    setIsProcessing(true)

    try {
      // Step 1: Categorize the feedback
      const categoryPrompt = `You are analyzing user feedback for Gingerbread Privacy, an executive privacy and threat intelligence platform.

User feedback: "${state.userMessage}"

Categorize this feedback into EXACTLY ONE of these categories:
- BUG: Technical issue, error, something broken
- FEATURE: New feature request or enhancement
- CONFUSION: User doesn't understand how something works
- PRAISE: Positive feedback, compliment, thank you
- OTHER: Doesn't fit other categories

Also generate 1-3 clarifying questions to better understand the issue.

Respond in this exact JSON format:
{
  "category": "BUG|FEATURE|CONFUSION|PRAISE|OTHER",
  "questions": ["question 1", "question 2"],
  "suggested_context": ["what browser they're using", "what page they were on"]
}

JSON only, no explanation:`

      const categoryResponse = await analyzeWithAI(categoryPrompt)

      // Parse AI response
      let parsed
      try {
        const jsonMatch = categoryResponse.match(/\{[\s\S]*\}/)
        parsed = JSON.parse(jsonMatch ? jsonMatch[0] : categoryResponse)
      } catch {
        parsed = {
          category: 'OTHER',
          questions: ['Can you provide more details about what you experienced?'],
          suggested_context: []
        }
      }

      if (parsed.category === 'PRAISE' || parsed.questions.length === 0) {
        // Skip clarifying questions for praise or if none generated
        setState(prev => ({
          ...prev,
          category: parsed.category,
          step: 'categorized',
          clarifyingQuestions: [],
          suggestedContext: parsed.suggested_context || []
        }))
      } else {
        setState(prev => ({
          ...prev,
          category: parsed.category,
          clarifyingQuestions: parsed.questions || [],
          suggestedContext: parsed.suggested_context || [],
          step: 'clarifying',
          currentQuestion: 0
        }))
      }
    } catch (error) {
      // Fallback to simple submission
      setState(prev => ({
        ...prev,
        category: 'OTHER',
        step: 'categorized'
      }))
    }

    setIsProcessing(false)
  }

  const handleAnswerSubmit = () => {
    const newAnswers = [...state.clarifyingAnswers, currentAnswer]

    if (state.currentQuestion + 1 < state.clarifyingQuestions.length) {
      setState(prev => ({
        ...prev,
        clarifyingAnswers: newAnswers,
        currentQuestion: prev.currentQuestion + 1
      }))
      setCurrentAnswer('')
    } else {
      setState(prev => ({
        ...prev,
        clarifyingAnswers: newAnswers,
        step: 'categorized'
      }))
      setCurrentAnswer('')
    }
  }

  const handleSkipQuestion = () => {
    const newAnswers = [...state.clarifyingAnswers, '(skipped)']

    if (state.currentQuestion + 1 < state.clarifyingQuestions.length) {
      setState(prev => ({
        ...prev,
        clarifyingAnswers: newAnswers,
        currentQuestion: prev.currentQuestion + 1
      }))
    } else {
      setState(prev => ({
        ...prev,
        clarifyingAnswers: newAnswers,
        step: 'categorized'
      }))
    }
  }

  const handleFinalSubmit = async () => {
    setIsProcessing(true)
    setState(prev => ({ ...prev, step: 'submitting' }))

    try {
      // Generate enriched summary
      const summaryPrompt = `Create a concise, well-structured summary for a Jira ticket based on this user feedback:

Original message: "${state.userMessage}"
Category: ${state.category}
${state.clarifyingQuestions.length > 0 ? `
Clarifying Q&A:
${state.clarifyingQuestions.map((q, i) => `Q: ${q}\nA: ${state.clarifyingAnswers[i] || '(no answer)'}`).join('\n\n')}
` : ''}

Create a summary in this format:
SUMMARY: [one-line summary]

DETAILS:
[structured details from the feedback]

SUGGESTED PRIORITY: [Low/Medium/High]

Keep it professional and actionable.`

      let enrichedSummary = ''
      try {
        enrichedSummary = await analyzeWithAI(summaryPrompt)
      } catch {
        enrichedSummary = state.userMessage
      }

      // Build Jira URL
      const currentPage = window.location.pathname
      const description = encodeURIComponent(
        `**Category:** ${state.category}\n` +
        `**User:** ${user?.email || 'Unknown'}\n` +
        `**Page:** ${currentPage}\n` +
        `**Browser:** ${navigator.userAgent}\n\n` +
        `---\n\n` +
        `**Original Feedback:**\n${state.userMessage}\n\n` +
        (state.clarifyingQuestions.length > 0 ?
          `**Clarifying Information:**\n${state.clarifyingQuestions.map((q, i) =>
            `- ${q}: ${state.clarifyingAnswers[i] || '(skipped)'}`
          ).join('\n')}\n\n` : '') +
        `---\n\n` +
        `**AI-Enriched Summary:**\n${enrichedSummary}`
      )

      const summary = encodeURIComponent(`[${state.category}] Feedback from ${currentPage}`)

      const jiraUrl = `https://monadnockcyber.atlassian.net/servicedesk/customer/portal/1/group/1/create/1?summary=${summary}&description=${description}`

      window.open(jiraUrl, '_blank', 'noopener,noreferrer')

      setState(prev => ({ ...prev, step: 'success' }))
    } catch (error) {
      setState(prev => ({ ...prev, step: 'error' }))
    }

    setIsProcessing(false)
  }

  if (!isOpen) return null

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'BUG': return 'text-red-400 bg-red-900/30'
      case 'FEATURE': return 'text-blue-400 bg-blue-900/30'
      case 'CONFUSION': return 'text-yellow-400 bg-yellow-900/30'
      case 'PRAISE': return 'text-green-400 bg-green-900/30'
      default: return 'text-gray-400 bg-gray-900/30'
    }
  }

  const getCategoryEmoji = (category: string | null) => {
    switch (category) {
      case 'BUG': return 'Bug Report'
      case 'FEATURE': return 'Feature Request'
      case 'CONFUSION': return 'Needs Clarification'
      case 'PRAISE': return 'Positive Feedback'
      default: return 'General Feedback'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-900 border border-dark-700 rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">
              <span className="text-ginger-500">AI-Assisted</span> Feedback
            </h2>
            <button
              onClick={onClose}
              className="text-dark-400 hover:text-dark-100 text-2xl"
            >
              &times;
            </button>
          </div>

          {/* Step: Initial */}
          {state.step === 'initial' && (
            <div>
              <p className="text-dark-300 mb-4">
                Tell us what's on your mind. Our AI will help categorize and enrich your feedback.
              </p>
              <textarea
                value={state.userMessage}
                onChange={(e) => setState(prev => ({ ...prev, userMessage: e.target.value }))}
                placeholder="Describe your feedback, issue, or suggestion..."
                className="w-full h-32 p-3 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:border-ginger-500 focus:outline-none resize-none"
                disabled={isProcessing}
              />
              <button
                onClick={handleInitialSubmit}
                disabled={isProcessing || !state.userMessage.trim()}
                className="mt-4 w-full py-3 bg-ginger-500 hover:bg-ginger-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Analyzing...
                  </span>
                ) : 'Continue'}
              </button>
            </div>
          )}

          {/* Step: Clarifying Questions */}
          {state.step === 'clarifying' && (
            <div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm mb-4 ${getCategoryColor(state.category)}`}>
                {getCategoryEmoji(state.category)}
              </div>

              <div className="mb-4">
                <p className="text-dark-300 mb-2">
                  Question {state.currentQuestion + 1} of {state.clarifyingQuestions.length}:
                </p>
                <p className="text-lg font-medium mb-4">
                  {state.clarifyingQuestions[state.currentQuestion]}
                </p>
              </div>

              <textarea
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder="Your answer..."
                className="w-full h-24 p-3 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:border-ginger-500 focus:outline-none resize-none"
              />

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleSkipQuestion}
                  className="flex-1 py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleAnswerSubmit}
                  className="flex-1 py-2 bg-ginger-500 hover:bg-ginger-600 text-black font-semibold rounded-lg transition-colors"
                >
                  {state.currentQuestion + 1 < state.clarifyingQuestions.length ? 'Next' : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Categorized - Ready to Submit */}
          {state.step === 'categorized' && (
            <div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm mb-4 ${getCategoryColor(state.category)}`}>
                {getCategoryEmoji(state.category)}
              </div>

              <div className="bg-dark-800 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-2">Your Feedback Summary:</h3>
                <p className="text-dark-300 text-sm">{state.userMessage}</p>

                {state.clarifyingAnswers.filter(a => a !== '(skipped)').length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dark-700">
                    <p className="text-xs text-dark-400 mb-2">Additional context provided:</p>
                    {state.clarifyingQuestions.map((q, i) => (
                      state.clarifyingAnswers[i] && state.clarifyingAnswers[i] !== '(skipped)' && (
                        <p key={i} className="text-sm text-dark-300">
                          <span className="text-dark-400">{q}</span><br/>
                          {state.clarifyingAnswers[i]}
                        </p>
                      )
                    ))}
                  </div>
                )}
              </div>

              <p className="text-dark-400 text-sm mb-4">
                Click submit to open our support portal with your enriched feedback.
              </p>

              <button
                onClick={handleFinalSubmit}
                disabled={isProcessing}
                className="w-full py-3 bg-ginger-500 hover:bg-ginger-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                Submit Feedback
              </button>
            </div>
          )}

          {/* Step: Submitting */}
          {state.step === 'submitting' && (
            <div className="text-center py-8">
              <svg className="animate-spin h-12 w-12 mx-auto text-ginger-500 mb-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <p className="text-dark-300">Enriching and submitting your feedback...</p>
            </div>
          )}

          {/* Step: Success */}
          {state.step === 'success' && (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-xl font-bold mb-2">Thank You!</h3>
              <p className="text-dark-300 mb-6">
                Your feedback has been submitted to our support team.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-ginger-500 hover:bg-ginger-600 text-black font-semibold rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Step: Error */}
          {state.step === 'error' && (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold mb-2">Something went wrong</h3>
              <p className="text-dark-300 mb-6">
                We couldn't submit your feedback. Please try again or email us directly.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setState(prev => ({ ...prev, step: 'categorized' }))}
                  className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
                <a
                  href="mailto:services@monadnockcyber.ai"
                  className="px-6 py-2 bg-ginger-500 hover:bg-ginger-600 text-black font-semibold rounded-lg transition-colors"
                >
                  Email Us
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
